import { I18NextPipe } from 'angular-i18next';
import { PagedResponse, QueryFiltersParams, QueryPagingParams } from 'mbs-ui-shared@models/Paging';
import {
  BackupHistory,
  BackupHistoryDayInfo,
  BackupHistoryRemote,
  BackupStatuses,
  HistoryOperationType,
  LocalBackupHistoryParams,
  RemoteBackupHistoryParams
} from 'mbs-ui-shared@models/backupHistory';
import { getBackupStatusText, prepareLocalStatusData } from './backupHistoryStatus';

export function convertLocalBackupHistoryParamsToRemote(filters: LocalBackupHistoryParams): RemoteBackupHistoryParams {
  return {
    backupStatuses: filters.issuesOnly
      ? [
          BackupStatuses.Failed,
          BackupStatuses.Interrupted,
          BackupStatuses.UnexpectedlyClosed,
          BackupStatuses.Warning,
          BackupStatuses.MissingRun
        ]
      : undefined,
    companyIds: filters.companies,
    search: filters.search,
    offset: filters.offset,
    limit: filters.limit,
    orderBy: filters.orderBy,
    ...(filters.periodMonth && filters.periodYear
      ? {
          month: filters.periodMonth,
          year: filters.periodYear
        }
      : {})
  };
}

export const prepareLocalBackupHistoryData = (
  params: QueryFiltersParams & QueryPagingParams,
  response: BackupHistoryRemote,
  i18N: I18NextPipe
): PagedResponse<BackupHistory> & { totalComputers?: number } => {
  return {
    data: response.computers
      .map((computer) => {
        const localComputer = {
          hid: computer.hid,
          name: computer.name,
          displayName: computer.displayName,
          dateLastSuccessBackup: computer.lastSuccessBackupUtcDate,
          user: computer.user
        };

        return [{ id: `${computer.hid}0`, computer: { ...localComputer }, isCardHeader: true } as BackupHistory].concat(
          computer.plans
            .map((plan) => {
              let days = prepareLocalStatusData(
                plan.days,
                i18N,
                String(params.startDate),
                typeof params.days === 'number' ? params.days : 0
              );

              const result = [
                {
                  id: `${computer.hid}${plan.id}`,
                  computer: { ...localComputer },
                  plan: {
                    id: plan.id,
                    name: plan?.name,
                    type: plan?.type,
                    lastBackupStatus: plan?.lastBackupStatus,
                    lastBackupStatusText: getBackupStatusText(plan?.lastBackupStatus, i18N),
                    dateLastBackup: plan?.lastBackupUtcDate,
                    dateLastSuccessBackup: plan?.lastSuccessBackupUtcDate,
                    days
                  }
                } as BackupHistory
              ];

              const hasVerificationStatusData = days.lastStatus.some((status) => status.verificationStatus);

              if (!hasVerificationStatusData) return result;

              const verificationDays = {
                worstStatus: days.worstStatus.map((status) => ({ verificationStatus: status.verificationStatus })),
                bestStatus: days.bestStatus.map((status) => ({ verificationStatus: status.verificationStatus })),
                lastStatus: days.lastStatus.map((status) => ({ verificationStatus: status.verificationStatus }))
              } as BackupHistoryDayInfo;

              days = {
                worstStatus: days.worstStatus.map((status) => ({ backupStatus: status.backupStatus })),
                bestStatus: days.bestStatus.map((status) => ({ backupStatus: status.backupStatus })),
                lastStatus: days.lastStatus.map((status) => ({ backupStatus: status.backupStatus }))
              } as BackupHistoryDayInfo;

              return [
                {
                  ...result[0],
                  plan: {
                    ...result[0].plan,
                    days
                  }
                } as BackupHistory,
                {
                  ...result[0],
                  id: `${result[0].id}rv`,
                  plan: {
                    ...result[0].plan,
                    isVerificationStatus: true,
                    days: verificationDays
                  }
                } as BackupHistory
              ];
            })
            .flat(1)
        );
      })
      .flat(),
    total: response.totalPlans,
    totalComputers: response.totalComputers
  };
};

export const historyOperationTypeTextMap: Map<HistoryOperationType, string> = new Map([
  [HistoryOperationType.All, ''],
  [HistoryOperationType.Backup, 'backup-history:historyOperationType.backup'],
  [HistoryOperationType.Restore, 'backup-history:historyOperationType.restore'],
  [HistoryOperationType.Purge, 'backup-history:historyOperationType.purge'],
  [HistoryOperationType.PreAction, 'backup-history:historyOperationType.preAction'],
  [HistoryOperationType.PostAction, 'backup-history:historyOperationType.postAction'],
  [HistoryOperationType.Failed, 'backup-history:historyOperationType.failed']
]);
